// Football Data API Service
// Handles communication with the backend API for real-time football data

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://your-backend-url.com' // Replace with your production URL
  : 'http://localhost:5001';

class FootballDataService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Generic API request method
  async makeRequest(endpoint, options = {}) {
    try {
      const url = `${this.baseURL}${endpoint}`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request error for ${endpoint}:`, error);
      throw error;
    }
  }

  // Get all competitions
  async getCompetitions() {
    return this.makeRequest('/api/competitions');
  }

  // Get matches for a specific competition
  async getCompetitionMatches(competitionCode, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/api/competitions/${competitionCode}/matches${queryString ? `?${queryString}` : ''}`;
    return this.makeRequest(endpoint);
  }

  // Get today's matches
  async getTodaysMatches() {
    return this.makeRequest('/api/matches/today');
  }

  // Get matches by date range
  async getMatches(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/api/matches${queryString ? `?${queryString}` : ''}`;
    return this.makeRequest(endpoint);
  }

  // Get specific match details
  async getMatch(matchId) {
    return this.makeRequest(`/api/matches/${matchId}`);
  }

  // Get team information
  async getTeam(teamId) {
    return this.makeRequest(`/api/teams/${teamId}`);
  }

  // Get areas (countries/regions)
  async getAreas() {
    return this.makeRequest('/api/areas');
  }

  // Get standings for a competition
  async getStandings(competitionCode, season = null) {
    const params = season ? `?season=${season}` : '';
    return this.makeRequest(`/api/competitions/${competitionCode}/standings${params}`);
  }

  // Get top scorers for a competition
  async getTopScorers(competitionCode, limit = 10, season = null) {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    if (season) params.append('season', season);
    const queryString = params.toString();
    return this.makeRequest(`/api/competitions/${competitionCode}/scorers${queryString ? `?${queryString}` : ''}`);
  }

  // Update ticket statuses based on real match data
  async updateTicketStatuses(tickets) {
    return this.makeRequest('/api/tickets/update-status', {
      method: 'POST',
      body: JSON.stringify({ tickets })
    });
  }

  // Health check
  async healthCheck() {
    return this.makeRequest('/api/health');
  }

  // Utility method to find matches by team names
  async findMatchesByTeams(homeTeam, awayTeam, date = null) {
    try {
      const params = {};
      if (date) {
        params.dateFrom = date;
        params.dateTo = date;
      }

      const matchesData = await this.getMatches(params);

      if (!matchesData.matches) return [];

      // Simple string matching for team names
      const homeTeamLower = homeTeam?.toLowerCase() || '';
      const awayTeamLower = awayTeam?.toLowerCase() || '';

      return matchesData.matches.filter(match => {
        const matchHomeTeam = match.homeTeam?.name?.toLowerCase() || '';
        const matchAwayTeam = match.awayTeam?.name?.toLowerCase() || '';

        return (
          matchHomeTeam.includes(homeTeamLower) ||
          matchAwayTeam.includes(awayTeamLower) ||
          homeTeamLower.includes(matchHomeTeam) ||
          awayTeamLower.includes(matchAwayTeam)
        );
      });
    } catch (error) {
      console.error('Error finding matches by teams:', error);
      return [];
    }
  }

  // Get live matches
  async getLiveMatches() {
    try {
      const matchesData = await this.getTodaysMatches();
      return matchesData.matches?.filter(match =>
        match.status === 'LIVE' || match.status === 'IN_PLAY'
      ) || [];
    } catch (error) {
      console.error('Error getting live matches:', error);
      return [];
    }
  }

  // Get upcoming matches
  async getUpcomingMatches() {
    try {
      const matchesData = await this.getTodaysMatches();
      return matchesData.matches?.filter(match =>
        match.status === 'SCHEDULED'
      ) || [];
    } catch (error) {
      console.error('Error getting upcoming matches:', error);
      return [];
    }
  }

  // Get finished matches
  async getFinishedMatches() {
    try {
      const matchesData = await this.getTodaysMatches();
      return matchesData.matches?.filter(match =>
        match.status === 'FINISHED'
      ) || [];
    } catch (error) {
      console.error('Error getting finished matches:', error);
      return [];
    }
  }
}

// Create and export singleton instance
const footballDataService = new FootballDataService();
export default footballDataService;
